// Resolve identifiers before querying the index so facets and pagination share
// the same exact result set, including explicitly bounded partial searches.
export function isCodeQuery(query) {
    const value = String(query || '').trim();
    return /\d/.test(value) && !/\s/.test(value);
}

export function createCodeSearchClient(client, resolve) {
    const cache = new Map();
    const resolutionFor = query => {
        const existing = cache.get(query);
        if (existing && existing.expires > Date.now()) return existing.promise;
        const promise = Promise.resolve().then(() => resolve(query)).then(result => {
            if (result?.mode !== 'code' || typeof result.filter_by !== 'string' || !result.filter_by) {
                throw new Error('Risposta ricerca codici non valida.');
            }
            return result;
        }).catch(error => { cache.delete(query); throw error; });
        if (cache.size >= 30) cache.delete(cache.keys().next().value);
        cache.set(query, { promise, expires: Date.now() + 15000 });
        return promise;
    };
    return {
        ...client,
        async search(requests) {
            const resolutions = [];
            const adapted = await Promise.all(requests.map(async (request, index) => {
                const query = String(request.params?.query || '').trim();
                if (!isCodeQuery(query)) return request;
                const result = await resolutionFor(query);
                resolutions[index] = result;
                return { ...request, params: { ...request.params, query: '',
                    filters: [request.params?.filters, result.filter_by].filter(Boolean)
                        .map(filter => `(${filter})`).join(' && '),
                } };
            }));
            const response = await client.search(adapted);
            return { ...response, results: response.results.map((result, index) => ({
                ...result, query: requests[index].params?.query || '',
                codeResolution: {
                    partial: resolutions[index]?.partial === true,
                    candidateLimit: resolutions[index]?.candidate_limit,
                },
            })) };
        },
    };
}

export async function resolveDashboardCode(work, query) {
    try {
        const response = await work.get('/search/resolve-code/', { q: query, allow_partial: true }, false, null,
            { ignoreGlobalErrorStatuses: [400, 403, 422, 503],
                ignoreSentryStatuses: [422] }); // 422: query non valida, errore atteso mostrato all'utente
        return response.data;
    } catch (error) {
        // Do not expose upstream internals or silently fall back to fuzzy codes.
        const status = error.response?.status;
        error.searchMessage = status === 422 ? error.response.data.detail
            : status === 400 ? 'Controlla il formato del codice cercato.'
                : status === 403 ? 'Non hai accesso alla ricerca codici.'
                    : 'Ricerca codici temporaneamente non disponibile. Riprova.';
        throw error;
    }
}

export function partialCodeSearchMessage(response) {
    const resolution = response?.results?.find(result => result.codeResolution?.partial)?.codeResolution;
    if (!resolution) return '';
    const limit = Number(resolution.candidateLimit).toLocaleString('it-IT');
    return `Ricerca parziale: considerati i primi ${limit} articoli e ODP. Conteggi e filtri si riferiscono a questo elenco. Aggiungi caratteri per cercare anche gli altri risultati.`;
}
